import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type {
  DRPartialProject,
  GetProjectsFilters,
  Project,
  ProjectPayload,
} from "@/types/projects.types";
import { ref, watch, type Ref } from "vue";
import { useDeviceState } from "../DeviceState";
import { projectRepository } from "@/repositories/project.repository";
import { storeToRefs } from "pinia";
import type { PaginationParams } from "@/types";

export const useGetProjects = (
  pagination: Ref<PaginationParams>,
  filters: Ref<GetProjectsFilters>,
  toSync = true,
) => {
  const localData = ref<DRPartialProject[]>([]);

  const getLocalData = async (params: GetProjectsFilters) => {
    localData.value = await projectRepository.getMany(params);
  };

  watch(
    filters,
    (newFilters) => {
      getLocalData(newFilters);
    },
    {
      immediate: true,
    },
  );

  const query = useQuery({
    queryKey: [QueryKey.PROJECTS, pagination, filters],
    queryFn: async (): Promise<{
      projects?: Project[];
      totalCount?: number;
    }> => {
      return await APIClient.fetchProjects({
        ...pagination.value,
        ...filters.value,
      }).then(async (data) => {
        if (toSync) await projectRepository.createManyProjects(data.projects);
        getLocalData(filters.value);
        return data;
      });
    },
    retry: 1,
  });

  return {
    ...query,
    localData,
  };
};

export const useGetProject = (id: Ref<number | undefined>) => {
  const deviceState = useDeviceState();
  const { isOnline } = storeToRefs(deviceState);
  return useQuery({
    queryKey: [QueryKey.PROJECTS, id],
    queryFn: async (): Promise<DRPartialProject | Project | undefined> => {
      if (!id.value) throw new Error("Project ID is required");
      if (isOnline.value) {
        return await APIClient.fetchProjectById(id.value);
      }
      return await projectRepository.getById(id.value);
    },
    enabled: !!id,
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      project: Partial<ProjectPayload> & {
        id: number;
      },
    ) => {
      const { id, ...payload } = project;
      return await APIClient.updateProject(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECTS] });
    },
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (project: ProjectPayload) => {
      return await APIClient.createProject(project);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECTS] });
    },
  });
};
